import { Alert, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import LoadingButton from '@/components/ui/LoadingButton/LoadingButton.tsx';
import { useTranslation } from 'react-i18next';
import BackButton from '@/components/ui/BackButton/BackButton.tsx';
import DeleteIcon from '@mui/icons-material/Delete';
import useIntegrationDelete from '@/api/useIntegrationDelete.ts';

type DeleteIntegrationModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: () => void;
  selectedElement: number | null | undefined;
};

function DeleteIntegrationModal({ isOpen, onClose, onConfirm, selectedElement }: DeleteIntegrationModalProps) {
  const { t } = useTranslation();
  const {
    mutateAsync: deleteIntegration,
    isError: isErrorDeleteIntegration,
    isPending: isLoadingDeleteIntegration,
    isSuccess: isSuccessDeleteIntegration,
    error: errorDeleteIntegration,
  } = useIntegrationDelete();

  const handleDeleteIntegration = async () => {
    if (!selectedElement) return;
    try {
      await deleteIntegration({ ids: [selectedElement] });
      if (onConfirm) {
        onConfirm();
      }
      onClose();
    } catch (error) {
      console.error('Error deleting integration: ', error);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('modals.deleteIntegration.title')}</DialogTitle>
      <DialogContent>
        <div className="flex flex-col gap-2 empty:hidden mb-2">
          {isSuccessDeleteIntegration && (
            <Alert severity="success">{t('modals.deleteIntegration.successMessage')}</Alert>
          )}
          {isErrorDeleteIntegration && <Alert severity="error">{errorDeleteIntegration?.message as string}</Alert>}
        </div>
        <DialogContentText>{t('modals.deleteIntegration.info')}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <div className="flex flex-wrap grow p-4 pt-0 justify-between">
          <BackButton onClick={onClose} autoFocus />
          <LoadingButton
            startIcon={<DeleteIcon />}
            loading={isLoadingDeleteIntegration}
            color="error"
            variant="contained"
            onClick={handleDeleteIntegration}
          >
            {t('button.delete')}
          </LoadingButton>
        </div>
      </DialogActions>
    </Dialog>
  );
}

export default DeleteIntegrationModal;
